import { ReactNode, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import IntranetChat from "@/components/IntranetChat";
import { useAuth } from "@/contexts/AuthContext";
import { useSessionTimeout } from "@/hooks/useSessionTimeout";
import {
  Building2, Ship, Users, Briefcase, Phone, LogOut, Settings,
  ChevronRight, Home, Megaphone, BookOpen, FileText, HelpCircle,
  BarChart3, GraduationCap, Scale, Handshake, AlertTriangle, Menu, ShoppingCart,
  Anchor, Timer, Star, Wrench, DollarSign, Leaf, Shield, PenTool, Calculator,
  Newspaper, Activity, Target, ClipboardList, CalendarDays, TrendingUp,
  ShieldCheck, FileSearch, Radar, Presentation, Library, UserPlus,
  Cog, Bug, CheckSquare, CloudSun, BookMarked, Heart, FileSignature, BarChart, ClipboardCheck, Sparkles, FlaskConical, MessageSquare
} from "lucide-react";
import { useState } from "react";
import velIoLogo from "@/assets/vel-io-logo.png";

interface NavItem {
  label: string;
  href: string;
  icon: React.ElementType;
  permKey: string;
}

interface NavSection {
  title: string;
  icon: React.ElementType;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    title: "Company Hub",
    icon: Building2,
    items: [
      { label: "Dashboard", href: "/employee", icon: Home, permKey: "page_dashboard" },
      { label: "Announcements", href: "/employee/announcements", icon: Megaphone, permKey: "page_announcements" },
      { label: "Org Chart", href: "/employee/directory", icon: Users, permKey: "page_directory" },
    ],
  },
  {
    title: "Operations",
    icon: Ship,
    items: [
      { label: "Voyage Management", href: "/employee/voyage-management", icon: Anchor, permKey: "page_voyage_ops" },
      { label: "Chem Carriage", href: "/employee/chem-carriage", icon: FlaskConical as any, permKey: "page_voyage_ops" },
      { label: "EAR / MAR Hub", href: "/employee/ear-mar", icon: ClipboardCheck, permKey: "page_ear_mar" },
      { label: "Maintenance & PMS", href: "/employee/maintenance", icon: Wrench, permKey: "page_maintenance" },
      { label: "Documents & SOPs", href: "/employee/documents", icon: FileText, permKey: "page_documents" },
      { label: "QHSE", href: "/employee/safety", icon: Shield, permKey: "page_safety" },
      { label: "Supply Chain", href: "/employee/supply-chain", icon: ShoppingCart, permKey: "page_supply_chain" },
    ],
  },
  {
    title: "People & HR",
    icon: Users,
    items: [
      { label: "Crew & Personnel", href: "/employee/crew-personnel", icon: Users, permKey: "page_crew_personnel" },
      { label: "Crew Roster", href: "/employee/crew-roster", icon: Users, permKey: "page_crew_roster" },
      { label: "Crew Scheduler", href: "/employee/crew-scheduler", icon: CalendarDays, permKey: "page_crew_roster" },
      { label: "Crew Welfare", href: "/employee/crew-welfare", icon: Heart, permKey: "page_crew_personnel" },
      { label: "Onboarding", href: "/employee/onboarding", icon: BookOpen, permKey: "page_onboarding" },
      { label: "Training & Certs", href: "/employee/training", icon: GraduationCap, permKey: "page_training" },
      { label: "Training Needs", href: "/employee/training-needs", icon: Target, permKey: "page_training" },
      { label: "JD / Offer Generator", href: "/employee/hr-generator", icon: FileSignature, permKey: "page_crew_personnel" },
    ],
  },
  {
    title: "Commercial",
    icon: Briefcase,
    items: [
      { label: "🤖 Commercial Hub", href: "/employee/commercial-hub", icon: Sparkles, permKey: "page_commercial" },
      { label: "✨ Commercial Value Center", href: "/employee/value-center", icon: Sparkles, permKey: "page_commercial" },
      { label: "Commercial & Finance", href: "/employee/commercial-finance", icon: DollarSign, permKey: "page_commercial" },
      { label: "Financial Pulse", href: "/employee/financial-pulse", icon: DollarSign, permKey: "page_commercial" },
      { label: "Rate Intelligence", href: "/employee/rate-intelligence", icon: TrendingUp, permKey: "page_commercial" },
      { label: "Crew Transfer Intelligence", href: "/employee/crew-transfer-intelligence", icon: TrendingUp, permKey: "page_commercial" },
      { label: "Contracts", href: "/employee/contracts", icon: Handshake, permKey: "page_contracts" },
      { label: "Contract Redline", href: "/employee/contract-redline", icon: FileSearch, permKey: "page_contracts" },
      { label: "Clause Library", href: "/employee/clause-library", icon: Library, permKey: "page_contracts" },
      { label: "Client Onboarding", href: "/employee/client-onboarding", icon: UserPlus, permKey: "page_contracts" },
      { label: "Partner Hub", href: "/employee/partner-hub", icon: Target, permKey: "page_contracts" },
      { label: "Fleet Intel", href: "/employee/fleet-intelligence", icon: Anchor, permKey: "page_fleet_intel" },
      { label: "Vessel Catalogue", href: "/employee/vessel-catalogue", icon: Ship, permKey: "page_vessel_catalogue" },
      { label: "Client Questions", href: "/employee/questions", icon: HelpCircle, permKey: "page_questions" },
      { label: "Project Updates", href: "/employee/progress", icon: BarChart3, permKey: "page_progress" },
      { label: "Satisfaction", href: "/employee/satisfaction", icon: Star, permKey: "page_satisfaction" },
      { label: "Reading Analytics", href: "/employee/reading-analytics", icon: Timer, permKey: "page_reading_analytics" },
    ],
  },
  {
    title: "Insights",
    icon: BarChart3,
    items: [
      { label: "Reports & Analytics", href: "/employee/reports-analytics", icon: BarChart3, permKey: "page_reports" },
      { label: "Board Report", href: "/employee/board-report", icon: BarChart, permKey: "page_reports" },
      { label: "ESG & Emissions", href: "/employee/esg-emissions", icon: Leaf, permKey: "page_esg" },
      { label: "Lessons Learned", href: "/employee/lessons-learned", icon: BookOpen, permKey: "page_lessons_learned" },
      { label: "VOFF Digest", href: "/employee/intelligence-digest", icon: Newspaper, permKey: "page_reports" },
      { label: "Regulatory Radar", href: "/employee/regulatory-radar", icon: Radar, permKey: "page_compliance" },
      { label: "Meeting Prep", href: "/employee/meeting-prep", icon: Presentation, permKey: "page_reports" },
    ],
  },
  {
    title: "Compliance",
    icon: Scale,
    items: [
      { label: "Compliance Calendar", href: "/employee/compliance", icon: Scale, permKey: "page_compliance" },
      { label: "OVMSA2 Assessment", href: "/employee/ovmsa-assessment", icon: ClipboardCheck, permKey: "page_compliance" },
      { label: "Audit Prep", href: "/employee/audit-prep", icon: BookMarked, permKey: "page_compliance" },
      { label: "Audit Room", href: "/employee/audit-room", icon: MessageSquare, permKey: "page_compliance" },
    ],
  },
  {
    title: "Communications",
    icon: Phone,
    items: [
      { label: "Emergency Contacts", href: "/employee/emergency-contacts", icon: AlertTriangle, permKey: "page_emergency_contacts" },
      { label: "Vendor Management", href: "/employee/vendors", icon: Handshake, permKey: "page_vendors" },
    ],
  },
];

const IntranetLayout = ({ children }: { children: ReactNode }) => {
  const { user, loading, signOut, isEmployee, isAdmin, fullName, employeePermissions } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  useSessionTimeout();

  const hasAccess = isEmployee || isAdmin;

  useEffect(() => {
    if (!loading && !user) navigate("/client-login");
    if (!loading && user && !hasAccess) navigate("/portal");
  }, [user, loading, hasAccess, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-teal/30 border-t-teal rounded-full animate-spin" />
      </div>
    );
  }

  if (!user || !hasAccess) return null;

  const isActive = (href: string) => location.pathname === href;

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-4 border-b border-border">
        <Link to="/employee" className="flex flex-col items-center gap-3">
          {sidebarOpen ? (
            <>
              <img src="/brand/velocity_offshore_full_color.png" alt="Velocity Offshore" className="h-14 w-auto object-contain" />
              <div className="w-full pt-3 border-t border-border flex flex-col items-center gap-1.5">
                <span className="text-[9px] tracking-[2px] uppercase text-muted-foreground font-body">Powered by</span>
                <img src="/vel-io-logo.png" alt="Vel-iO" className="h-7 w-auto object-contain" />
              </div>
            </>
          ) : (
            <img src="/vel-io-logo.png" alt="Vel-iO" className="h-8 w-auto object-contain" />
          )}
        </Link>
      </div>

      {/* Nav sections */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-5">
        {navSections.map((section) => {
          const SectionIcon = section.icon;
          // Filter items by permission (admins see all)
          const visibleItems = isAdmin
            ? section.items
            : section.items.filter((item) => (employeePermissions as any)[item.permKey] !== false);
          if (visibleItems.length === 0) return null;
          return (
            <div key={section.title}>
              <div className="flex items-center gap-2 px-3 mb-2">
                <SectionIcon className="w-3.5 h-3.5 text-muted-foreground" />
                {sidebarOpen && (
                  <span className="text-[10px] font-bold tracking-[2px] uppercase text-muted-foreground font-body">
                    {section.title}
                  </span>
                )}
              </div>
              <div className="space-y-0.5">
                {visibleItems.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setMobileSidebarOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-body font-medium transition-all duration-200 ${
                        active
                          ? "bg-teal/10 text-teal border-l-2 border-teal"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      }`}
                    >
                      <Icon className={`w-4 h-4 flex-shrink-0 ${active ? "text-teal" : ""}`} />
                      {sidebarOpen && <span>{item.label}</span>}
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* User footer */}
      <div className="p-4 border-t border-border">
        <Link
          to="/employee/settings"
          onClick={() => setMobileSidebarOpen(false)}
          className="flex items-center gap-3 p-1.5 rounded-lg hover:bg-muted transition-colors group"
        >
          <div className="w-8 h-8 rounded-full bg-teal/10 flex items-center justify-center">
            <span className="text-xs font-bold text-teal font-body">
              {(fullName || user?.email || "U").charAt(0).toUpperCase()}
            </span>
          </div>
          {sidebarOpen && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground font-body truncate">{fullName || user?.email}</p>
              <p className="text-[10px] text-muted-foreground font-body">{isAdmin ? "Administrator" : "Employee"}</p>
            </div>
          )}
        </Link>
        <div className="flex items-center gap-2 mt-2">
          <Link
            to="/employee/settings"
            onClick={() => setMobileSidebarOpen(false)}
            className="flex-1 flex items-center justify-center gap-1.5 p-1.5 rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-all font-body"
          >
            <Settings className="w-3.5 h-3.5" />
            {sidebarOpen && <span>Settings</span>}
          </Link>
          <button
            onClick={() => signOut().then(() => navigate("/"))}
            className="flex-1 flex items-center justify-center gap-1.5 p-1.5 rounded-lg text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-all font-body"
            title="Sign Out"
          >
            <LogOut className="w-3.5 h-3.5" />
            {sidebarOpen && <span>Sign Out</span>}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside
        className={`hidden lg:flex flex-col bg-card border-r border-border transition-all duration-300 ${
          sidebarOpen ? "w-64" : "w-16"
        } fixed top-0 left-0 h-screen z-30`}
      >
        {sidebarContent}
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileSidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-foreground/20 backdrop-blur-sm" onClick={() => setMobileSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 bg-card shadow-xl">
            {sidebarContent}
          </aside>
        </div>
      )}

      {/* Main content */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarOpen ? "lg:ml-64" : "lg:ml-16"}`}>
        {/* Top bar */}
        <header className="sticky top-0 z-20 bg-card/80 backdrop-blur-lg border-b border-border">
          <div className="flex items-center justify-between px-6 py-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  if (window.innerWidth < 1024) setMobileSidebarOpen(true);
                  else setSidebarOpen(!sidebarOpen);
                }}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
              >
                <Menu className="w-5 h-5 text-muted-foreground" />
              </button>
              <nav className="hidden md:flex items-center gap-1 text-sm text-muted-foreground font-body">
                <Link to="/employee" className="hover:text-foreground transition-colors">Vel-iO</Link>
                {location.pathname !== "/employee" && (
                  <>
                    <ChevronRight className="w-3.5 h-3.5" />
                    <span className="text-foreground font-medium">
                      {navSections.flatMap(s => s.items).find(i => i.href === location.pathname)?.label || "Page"}
                    </span>
                  </>
                )}
              </nav>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/"
                className="text-xs text-muted-foreground hover:text-foreground font-body px-3 py-1.5 rounded-lg hover:bg-muted transition-all"
              >
                Main Site
              </Link>
              {isAdmin && (
                <Link
                  to="/admin"
                  className="text-xs text-teal hover:text-teal-light font-body font-medium px-3 py-1.5 rounded-lg hover:bg-teal/5 transition-all"
                >
                  Admin Panel
                </Link>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 lg:p-8">
          <div className="max-w-[1400px] mx-auto">
            {children}
          </div>
        </main>
      </div>

      {/* Team Chat */}
      <IntranetChat />
    </div>
  );
};

export default IntranetLayout;
